/**
 * SeanStudio — Sean's private cross-posting cockpit.
 *
 * Flow:
 *   1. Sean opens  /#sean  →  passcode gate.
 *   2. Writes caption (+ optional YouTube / Instagram URL + optional link).
 *   3. Hits "Publish & Blast" → post goes live on the public feed instantly.
 *   4. A share deck pops up with one-tap buttons that deep-link to every
 *      social platform's native composer, with the caption pre-filled.
 *      (True auto-posting to IG/TikTok/YouTube needs each platform's OAuth
 *      tokens — until Sean connects a Business account, these buttons are
 *      the fastest humanly-possible path.)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { seanLogin, seanCreatePost, seanListPosts, seanDeletePost, seanUploadFile, getStories, mediaSrc } from "@/lib/api";

const POST_TYPES = [
  { key: "post", label: "📝 Post", hint: "Caption + optional photo/video. Shows in Fresh From The Lab." },
  { key: "reel", label: "🎬 Reel", hint: "A vertical video. Plays in the full-screen Reels feed." },
  { key: "story", label: "⭕ Story", hint: "A photo or short clip. Disappears after 24 hours." },
];
const EMPTY_COMPOSER = { caption: "", media_url: "", link_url: "", tags: "", post_type: "post" };

const SEAN_KEY_STORAGE = "seanlab_sean_key";

const APP_URL = (typeof window !== "undefined" && window.location.origin) || "https://seanthesciencekidofficial.com";

/* ============ Helpers ============ */
const detectMediaKind = (url = "") => {
  const u = url.trim();
  if (!u) return null;
  if (/youtu\.be\/|youtube\.com\/(watch|shorts|embed)/i.test(u)) return "youtube";
  if (/instagram\.com\/(reel|p|reels)\//i.test(u)) return "instagram";
  if (/tiktok\.com\//i.test(u)) return "tiktok";
  return "link";
};

const extractYouTubeId = (url) => {
  if (!url) return null;
  const m =
    url.match(/[?&]v=([^&]+)/) ||
    url.match(/youtu\.be\/([^?&]+)/) ||
    url.match(/\/shorts\/([^?&]+)/) ||
    url.match(/\/embed\/([^?&]+)/);
  return m ? m[1] : null;
};

/* ============ Component ============ */
export default function SeanStudio({ onExit }) {
  const [seanKey, setSeanKey] = useState(() => sessionStorage.getItem(SEAN_KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [err, setErr] = useState("");
  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [composer, setComposer] = useState(EMPTY_COMPOSER);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [shareFor, setShareFor] = useState(null); // { post, file } → opens Blast deck

  const tryLogin = useCallback(async (key) => {
    setErr("");
    try {
      await seanLogin(key);
      sessionStorage.setItem(SEAN_KEY_STORAGE, key);
      setSeanKey(key);
      setAuthed(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Wrong passcode.");
      sessionStorage.removeItem(SEAN_KEY_STORAGE);
      setAuthed(false);
    }
  }, []);

  useEffect(() => { if (seanKey && !authed) tryLogin(seanKey); }, [seanKey, authed, tryLogin]);

  const refreshPosts = useCallback(async () => {
    try {
      const [data, st] = await Promise.all([seanListPosts(25), getStories()]);
      setPosts(data.items || []);
      setStories(st.items || []);
    } catch {/* ignore */}
  }, []);

  useEffect(() => { if (authed) refreshPosts(); }, [authed, refreshPosts]);

  const type = composer.post_type;
  const isVideo = file?.type?.startsWith("video/");
  const needsFile = type !== "post";
  const canPublish =
    !publishing &&
    (type === "story" ? !!file : composer.caption.trim().length > 0) &&
    (!needsFile || !!file) &&
    (type !== "reel" || isVideo);

  const resetComposer = () => { setComposer(EMPTY_COMPOSER); setFile(null); setProgress(0); };

  const publish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    setErr("");
    try {
      const tags = composer.tags
        .split(/[\s,]+/)
        .map((t) => t.trim())
        .filter(Boolean);
      const uploaded = file ? await seanUploadFile(seanKey, file, setProgress) : null;
      const post = await seanCreatePost(seanKey, {
        caption: composer.caption.trim(),
        media_url: composer.media_url.trim() || null,
        link_url: composer.link_url.trim() || null,
        upload_url: uploaded?.url || null,
        post_type: type,
        tags,
      });
      const sentFile = file;
      resetComposer();
      await refreshPosts();
      setShareFor({ post, file: sentFile });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Publish failed — check your connection and try again.");
    } finally {
      setPublishing(false);
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post from the public feed?")) return;
    try {
      await seanDeletePost(seanKey, id);
      await refreshPosts();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed.");
    }
  };

  /* ============ LOGIN WALL ============ */
  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--paper)" }}>
        <div
          className="card-flat w-full max-w-md"
          style={{ border: "3px solid var(--ink)", boxShadow: "6px 6px 0 0 var(--ink)", background: "white" }}
          data-testid="sean-studio-gate"
        >
          <div className="eyebrow text-[var(--orange)] mb-2">🧪 sean's studio</div>
          <h2 className="font-display text-2xl text-[var(--ink)] mb-1">Private — Sean only</h2>
          <p className="text-sm text-[var(--ink-soft)] mb-5">
            Post once here → blast to every platform in one tap.
          </p>
          <form
            onSubmit={(e) => { e.preventDefault(); tryLogin(pwInput); }}
            className="space-y-3"
          >
            <input
              type="password"
              value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              placeholder="Studio passcode"
              className="field w-full"
              autoFocus
              data-testid="sean-studio-pw"
            />
            {err && <div className="text-sm font-bold text-[var(--red)]" data-testid="sean-studio-err">{err}</div>}
            <button type="submit" className="btn btn-red w-full" data-testid="sean-studio-login">
              Enter Studio →
            </button>
          </form>
          <button
            onClick={onExit}
            className="btn-ghost w-full mt-3 text-xs"
            data-testid="sean-studio-exit"
          >
            ← back to the lab
          </button>
        </div>
      </div>
    );
  }

  /* ============ MAIN STUDIO ============ */
  return (
    <div className="min-h-screen pb-10" style={{ background: "var(--paper)" }}>
      <header
        className="sticky top-0 z-30 border-b-[3px] border-[var(--ink)]"
        style={{ background: "var(--orange)" }}
      >
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <div className="eyebrow text-[var(--ink)] opacity-80">🧪 private · sean only</div>
            <h1 className="font-display text-xl sm:text-2xl text-[var(--ink)]">Sean's Post Studio</h1>
          </div>
          <button
            onClick={() => { sessionStorage.removeItem(SEAN_KEY_STORAGE); onExit(); }}
            className="btn-ghost text-sm whitespace-nowrap"
            style={{ background: "white", border: "2.5px solid var(--ink)" }}
            data-testid="sean-studio-logout"
          >
            Lock & exit
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* === COMPOSER === */}
        <section
          className="card-flat bg-white"
          style={{ border: "3px solid var(--ink)", boxShadow: "5px 5px 0 0 var(--ink)" }}
          data-testid="sean-composer"
        >
          <div className="eyebrow text-[var(--red)] mb-2">// make something</div>
          <h2 className="font-display text-lg text-[var(--ink)] mb-3">What's cooking in the lab, Sean?</h2>

          <div className="grid grid-cols-3 gap-2 mb-2" data-testid="sean-type-picker">
            {POST_TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setComposer((c) => ({ ...c, post_type: t.key }))}
                className="rounded-xl py-2.5 text-sm font-bold transition-transform active:scale-95"
                style={{
                  background: type === t.key ? "var(--orange)" : "#fff",
                  border: "2.5px solid var(--ink)",
                  boxShadow: type === t.key ? "3px 3px 0 0 var(--ink)" : "none",
                }}
                data-testid={`sean-type-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-[0.72rem] text-[var(--ink-soft)] mb-3">{POST_TYPES.find((t) => t.key === type).hint}</p>

          <MediaPicker file={file} setFile={setFile} type={type} disabled={publishing} />
          {type === "reel" && file && !isVideo && (
            <div className="mt-2 text-xs font-bold text-[var(--red)]">Reels need a video — pick a video or switch to Post.</div>
          )}

          <textarea
            value={composer.caption}
            onChange={(e) => setComposer((c) => ({ ...c, caption: e.target.value }))}
            placeholder={type === "story" ? "Optional caption for your story…" : "e.g. New video drops tonight! We built a working neuron out of jello 🧠⚡"}
            maxLength={2200}
            rows={type === "story" ? 2 : 4}
            className="field w-full resize-none mt-3"
            data-testid="sean-composer-caption"
          />
          <div className="text-right text-[0.65rem] font-mono text-[var(--ink-soft)] mt-1">
            {composer.caption.length}/2200
          </div>

          {type === "post" && (
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <label className="block">
              <span className="label-tag block mb-1">// or paste a video / reel URL (optional)</span>
              <input
                type="url"
                value={composer.media_url}
                onChange={(e) => setComposer((c) => ({ ...c, media_url: e.target.value }))}
                placeholder="YouTube, Instagram reel, TikTok…"
                className="field w-full"
                data-testid="sean-composer-media"
              />
            </label>
            <label className="block">
              <span className="label-tag block mb-1">// extra link (optional)</span>
              <input
                type="url"
                value={composer.link_url}
                onChange={(e) => setComposer((c) => ({ ...c, link_url: e.target.value }))}
                placeholder="https://…"
                className="field w-full"
                data-testid="sean-composer-link"
              />
            </label>
          </div>
          )}

          <label className="block mt-3">
            <span className="label-tag block mb-1">// hashtags (space-separated, no #)</span>
            <input
              type="text"
              value={composer.tags}
              onChange={(e) => setComposer((c) => ({ ...c, tags: e.target.value }))}
              placeholder="science seanthesciencekid neurocardio"
              className="field w-full"
              data-testid="sean-composer-tags"
            />
          </label>

          {err && <div className="mt-3 text-sm font-bold text-[var(--red)]" data-testid="sean-composer-error">{err}</div>}

          {publishing && file && (
            <div className="mt-3" data-testid="sean-upload-progress">
              <div className="h-3 rounded-full overflow-hidden" style={{ border: "2px solid var(--ink)", background: "#fff" }}>
                <div className="h-full transition-[width] duration-300" style={{ width: `${progress}%`, background: "var(--green)" }} />
              </div>
              <div className="text-[0.7rem] font-mono text-[var(--ink-soft)] mt-1">uploading… {progress}%</div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={publish}
              disabled={!canPublish}
              className="btn btn-red"
              data-testid="sean-composer-publish"
            >
              {publishing ? "Publishing…" : type === "story" ? "⭕ Post Story & Blast" : type === "reel" ? "🎬 Post Reel & Blast" : "🚀 Publish & Blast"}
            </button>
            <button
              onClick={resetComposer}
              disabled={publishing || (!composer.caption && !composer.media_url && !composer.link_url && !composer.tags && !file)}
              className="btn-ghost"
              data-testid="sean-composer-clear"
            >
              Clear
            </button>
          </div>
          <p className="mt-3 text-[0.7rem] font-mono text-[var(--ink-soft)]">
            Posts appear instantly on the public feed. The share deck opens so you can blast it to every platform in one tap.
          </p>
        </section>

        {/* === LIVE STORIES === */}
        {stories.length > 0 && (
          <section data-testid="sean-live-stories">
            <h3 className="font-display text-lg text-[var(--ink)] mb-3">Live stories ({stories.length}) · vanish after 24h</h3>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {stories.map((s) => (
                <div key={s.id} className="shrink-0 w-24 text-center">
                  <div className="w-24 h-36 rounded-xl overflow-hidden mb-1" style={{ border: "2.5px solid var(--ink)" }}>
                    <MediaThumb post={s} />
                  </div>
                  <div className="flex gap-1 justify-center">
                    <button onClick={() => setShareFor({ post: s })} className="text-[0.65rem] font-bold text-[var(--red)]" data-testid={`sean-story-share-${s.id}`}>share</button>
                    <span className="text-[0.65rem]">·</span>
                    <button onClick={() => deletePost(s.id)} className="text-[0.65rem] font-bold text-[var(--ink-soft)]" data-testid={`sean-story-delete-${s.id}`}>delete</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* === RECENT POSTS === */}
        <section data-testid="sean-recent-posts">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg text-[var(--ink)]">Recent posts ({posts.length})</h3>
            <button onClick={refreshPosts} className="btn-ghost text-xs">↻ refresh</button>
          </div>
          {posts.length === 0 ? (
            <div className="card-flat tint-cream text-center text-sm text-[var(--ink-soft)] py-8">
              No posts yet — the first one is yours, Sean 🧡
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map((p) => (
                <div
                  key={p.id}
                  className="card-flat bg-white"
                  style={{ border: "2.5px solid var(--ink)", boxShadow: "3px 3px 0 0 var(--ink)" }}
                >
                  <div className="text-xs font-mono text-[var(--ink-soft)] mb-1">
                    {p.post_type === "reel" ? "🎬 reel · " : ""}{new Date(p.created_at).toLocaleString()}
                  </div>
                  {p.upload_url && (
                    <div className="w-full max-w-[220px] rounded-xl overflow-hidden mb-2" style={{ border: "2.5px solid var(--ink)", aspectRatio: p.post_type === "reel" ? "9 / 16" : "4 / 3" }}>
                      <MediaThumb post={p} />
                    </div>
                  )}
                  <p className="text-sm text-[var(--ink)] whitespace-pre-wrap">{p.caption}</p>
                  {p.media_url && (
                    <a href={p.media_url} target="_blank" rel="noreferrer noopener" className="block mt-2 text-xs font-bold text-[var(--red)] break-all">
                      🎥 {p.media_url}
                    </a>
                  )}
                  {p.link_url && (
                    <a href={p.link_url} target="_blank" rel="noreferrer noopener" className="block mt-1 text-xs font-bold text-[var(--red)] break-all">
                      🔗 {p.link_url}
                    </a>
                  )}
                  {p.tags && p.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.tags.map((t) => <span key={t} className="pill text-[0.6rem]">#{t}</span>)}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => setShareFor({ post: p })}
                      className="btn btn-red text-xs"
                      data-testid={`sean-post-share-${p.id}`}
                    >
                      📣 Re-blast
                    </button>
                    <button
                      onClick={() => deletePost(p.id)}
                      className="btn-ghost text-xs"
                      style={{ color: "var(--red)" }}
                      data-testid={`sean-post-delete-${p.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {shareFor && <BlastDeck post={shareFor.post} file={shareFor.file} onClose={() => setShareFor(null)} />}
    </div>
  );
}

/* ============ Media picker (camera roll / camera) ============ */
function MediaPicker({ file, setFile, type, disabled }) {
  const inputRef = useRef(null);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const [tooBig, setTooBig] = useState(false);

  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 100 * 1024 * 1024) { setTooBig(true); return; }
    setTooBig(false);
    setFile(f);
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={type === "reel" ? "video/*" : "image/*,video/*"}
        onChange={pick}
        className="hidden"
        data-testid="sean-media-input"
      />
      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="w-full rounded-2xl py-6 flex flex-col items-center justify-center gap-1 transition-transform active:scale-[0.98]"
          style={{ border: "3px dashed var(--ink)", background: "var(--paper-2, #fff4dc)" }}
          data-testid="sean-media-pick-btn"
        >
          <span className="text-3xl" aria-hidden>{type === "reel" ? "🎬" : "📸"}</span>
          <span className="font-display text-base text-[var(--ink)]">
            {type === "reel" ? "Add a video" : type === "story" ? "Add a photo or clip" : "Add a photo or video (optional)"}
          </span>
          <span className="text-[0.7rem] text-[var(--ink-soft)]">camera or camera roll · up to 100 MB</span>
        </button>
      ) : (
        <div className="relative rounded-2xl overflow-hidden mx-auto" style={{ border: "3px solid var(--ink)", maxWidth: 280, background: "#0e1230" }} data-testid="sean-media-preview">
          {file.type.startsWith("video/") ? (
            <video src={preview} controls playsInline className="w-full block" style={{ maxHeight: 420 }} />
          ) : (
            <img src={preview} alt="preview" className="w-full block" style={{ maxHeight: 420, objectFit: "contain" }} />
          )}
          <button
            type="button"
            onClick={() => setFile(null)}
            disabled={disabled}
            className="absolute top-2 right-2 w-8 h-8 rounded-full font-bold"
            style={{ background: "#fff", border: "2px solid var(--ink)" }}
            aria-label="Remove file"
            data-testid="sean-media-remove-btn"
          >
            ✕
          </button>
        </div>
      )}
      {tooBig && <div className="mt-2 text-xs font-bold text-[var(--red)]" data-testid="sean-media-too-big">That file is over 100 MB — try a shorter clip.</div>}
    </div>
  );
}

function MediaThumb({ post }) {
  const src = mediaSrc(post.upload_url);
  return post.upload_kind === "video"
    ? <video src={`${src}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
    : <img src={src} alt="" className="w-full h-full object-cover" />;
}

/* ============ BLAST DECK (share overlay) ============ */
function BlastDeck({ post, file: givenFile, onClose }) {
  const permalink = `${APP_URL}/#post-${post.id}`;
  const tagsStr = (post.tags || []).map((t) => `#${t}`).join(" ");
  const fullText = `${post.caption}${tagsStr ? "\n\n" + tagsStr : ""}\n\n${post.media_url || post.link_url || permalink}`;
  const encoded = encodeURIComponent(fullText);
  const permalinkEnc = encodeURIComponent(permalink);
  const captionEnc = encodeURIComponent(post.caption);
  const fileUrl = mediaSrc(post.upload_url);

  // The actual photo/video, so the phone's share sheet can hand it straight to Instagram / TikTok / etc.
  const [file, setFile] = useState(givenFile || null);
  useEffect(() => {
    if (file || !fileUrl) return;
    let alive = true;
    fetch(fileUrl)
      .then((r) => r.blob())
      .then((b) => {
        if (!alive) return;
        const ext = (b.type.split("/")[1] || "bin").split(";")[0];
        setFile(new File([b], `sean-the-science-kid-${post.id.slice(0, 6)}.${ext}`, { type: b.type }));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [file, fileUrl, post.id]);
  const canShareFile = !!file && typeof navigator !== "undefined" && navigator.canShare?.({ files: [file] });

  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {/* ignore */}
  };

  const shareFile = async () => {
    copy();
    try {
      await navigator.share({ files: [file], text: fullText });
    } catch {/* user cancelled */}
  };

  const nativeShare = async () => {
    if (canShareFile) return shareFile();
    if (navigator.share) {
      try {
        await navigator.share({ text: fullText, url: post.media_url || post.link_url || permalink });
      } catch {/* user cancelled */}
    } else {
      copy();
    }
  };

  // With a real file, the share sheet is the only way to land it in the IG/TikTok/YouTube apps.
  const openApp = (webUrl) => () => {
    if (canShareFile) return shareFile();
    copy();
    window.open(webUrl, "_blank", "noopener,noreferrer");
  };
  const openInstagram = openApp("https://www.instagram.com/seanthesciencekidofficial");
  const openTikTok = openApp("https://www.tiktok.com/upload");
  const openYouTube = openApp("https://studio.youtube.com/");

  const buttons = [
    { key: "native",   label: "📲 Device Share",  color: "var(--orange)", onClick: nativeShare },
    { key: "ig",       label: "📸 Instagram",     color: "#E1306C",        onClick: openInstagram },
    { key: "fb",       label: "👍 Facebook",      color: "#1877F2",        href: `https://www.facebook.com/sharer/sharer.php?u=${permalinkEnc}&quote=${captionEnc}` },
    { key: "x",        label: "✖ X / Twitter",    color: "#000000",        href: `https://twitter.com/intent/tweet?text=${encoded}` },
    { key: "threads",  label: "🧵 Threads",       color: "#000000",        href: `https://www.threads.net/intent/post?text=${encoded}` },
    { key: "whatsapp", label: "💬 WhatsApp",      color: "#25D366",        href: `https://wa.me/?text=${encoded}` },
    { key: "messenger",label: "💌 Messenger",     color: "#0078FF",        href: `fb-messenger://share?link=${permalinkEnc}` },
    { key: "tiktok",   label: "🎵 TikTok",        color: "#010101",        onClick: openTikTok },
    { key: "youtube",  label: "▶ YouTube Studio", color: "#FF0000",        onClick: openYouTube },
    { key: "email",    label: "✉ Email",          color: "#333333",        href: `mailto:?subject=${encodeURIComponent("New from Sean The Science Kid")}&body=${encoded}` },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(14, 18, 48, 0.75)" }}
      onClick={onClose}
      data-testid="sean-blast-deck"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg bg-white"
        style={{
          border: "3px solid var(--ink)",
          boxShadow: "6px 6px 0 0 var(--ink)",
          borderRadius: "20px 20px 0 0",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div className="sticky top-0 z-10 px-4 sm:px-6 py-4 border-b-[3px] border-[var(--ink)] flex items-center justify-between" style={{ background: "var(--sun)" }}>
          <div>
            <div className="eyebrow text-[var(--red)]">// 🚀 blast it everywhere</div>
            <h3 className="font-display text-lg text-[var(--ink)]">One tap → every platform</h3>
          </div>
          <button onClick={onClose} className="text-xl font-bold px-2" aria-label="Close" data-testid="sean-blast-close">✕</button>
        </div>

        <div className="px-4 sm:px-6 py-5 space-y-4">
          <div className="card-flat tint-cream">
            <div className="eyebrow text-[var(--ink-soft)] mb-1">// preview</div>
            {fileUrl && (
              <div className="w-32 rounded-xl overflow-hidden mb-2" style={{ border: "2.5px solid var(--ink)", aspectRatio: "9 / 16" }}>
                <MediaThumb post={post} />
              </div>
            )}
            <p className="text-sm text-[var(--ink)] whitespace-pre-wrap">{post.caption}</p>
            {post.media_url && <div className="text-xs mt-2 font-mono text-[var(--red)] break-all">🎥 {post.media_url}</div>}
            {post.tags?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {post.tags.map((t) => <span key={t} className="pill text-[0.6rem]">#{t}</span>)}
              </div>
            )}
          </div>

          {fileUrl && (
            <div className="grid grid-cols-1 gap-2" data-testid="sean-blast-file-actions">
              {canShareFile && (
                <button
                  onClick={shareFile}
                  className="btn w-full text-white"
                  style={{ background: "linear-gradient(90deg,#E1306C,#FF7A00)", border: "2.5px solid var(--ink)" }}
                  data-testid="sean-blast-share-file"
                >
                  📲 Share this {post.upload_kind === "video" ? "video" : "photo"} → Instagram, TikTok, YouTube…
                </button>
              )}
              <a
                href={fileUrl}
                download={`sean-the-science-kid-${post.id.slice(0, 6)}`}
                className="btn-ghost w-full text-center text-sm"
                data-testid="sean-blast-save-file"
              >
                ⬇ Save {post.upload_kind === "video" ? "video" : "photo"} to phone
              </a>
            </div>
          )}

          <button
            onClick={copy}
            className="btn w-full"
            style={{ background: copied ? "var(--green)" : "var(--ink)", color: "white" }}
            data-testid="sean-blast-copy"
          >
            {copied ? "✓ Copied — paste anywhere" : "📋 Copy caption + link"}
          </button>

          <div className="grid grid-cols-2 gap-2">
            {buttons.map((b) => (
              b.href ? (
                <a
                  key={b.key}
                  href={b.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="rounded-xl py-3 px-2 text-sm font-bold text-center text-white"
                  style={{ background: b.color, border: "2.5px solid var(--ink)", boxShadow: "2px 2px 0 0 var(--ink)" }}
                  data-testid={`sean-blast-${b.key}`}
                >
                  {b.label}
                </a>
              ) : (
                <button
                  key={b.key}
                  onClick={b.onClick}
                  className="rounded-xl py-3 px-2 text-sm font-bold text-center text-white"
                  style={{ background: b.color, border: "2.5px solid var(--ink)", boxShadow: "2px 2px 0 0 var(--ink)" }}
                  data-testid={`sean-blast-${b.key}`}
                >
                  {b.label}
                </button>
              )
            ))}
          </div>

          <div className="card-flat" style={{ background: "#fff1de", border: "2.5px solid var(--ink)" }}>
            <div className="eyebrow text-[var(--red)] mb-1">// heads-up</div>
            <p className="text-xs text-[var(--ink)] leading-relaxed">
              Instagram, TikTok and YouTube don't allow posting from other websites. On your phone, the <strong>Share this video/photo</strong> button sends the actual file to their apps (pick Reels, Story or Post there), and your caption is already copied, so just paste. X, Threads, Facebook, WhatsApp, Messenger and Email open with the text pre-filled.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
